import { useState } from "react";
import { useCustom, useCustomMutation, useTranslate } from "@refinedev/core";
import { Button, CheckboxField, Field, Modal, SelectField } from "@fawkes/ui";

/**
 * Een dier aanmaken bij een klant.
 *
 * <p>Bewust klein: alleen naam en diersoort zijn verplicht, want dat zijn ook de enige twee kolommen
 * op {@code dier} zonder standaardwaarde. Wat een balie niet weet hoort ze niet te moeten verzinnen —
 * ras, geslacht, geboortedatum en chip zijn later in het dossier bij te vullen.
 *
 * <p>Dit is <b>niet</b> de inboek-wizard: die maakt naast het dier ook een asielverblijf, en een
 * pensiongast woont bij zijn eigen baas.
 */

interface OptieRij {
  id: number;
  naam?: string;
  label?: string;
}

interface Formulier {
  naam: string;
  diersoortId: string;
  rasId: string;
  geslacht: string;
  gecastreerd: boolean;
  geboortedatum: string;
  chipPasnummer: string;
}

const LEEG: Formulier = {
  naam: "",
  diersoortId: "",
  rasId: "",
  geslacht: "",
  gecastreerd: false,
  geboortedatum: "",
  chipPasnummer: "",
};

type Melding = "verplicht" | "chip" | "verboden" | "fout" | null;

/** De referentielijsten heten hier {@code naam} en daar {@code label}; allebei aankunnen scheelt gedoe. */
function opties(rijen: OptieRij[]) {
  return rijen.map((r) => ({ value: String(r.id), label: r.naam ?? r.label ?? String(r.id) }));
}

export function DierCreateDialog({
  klantId,
  onClose,
  onGemaakt,
}: {
  /** De eigenaar; het nieuwe dier hangt er meteen aan. */
  klantId: number;
  onClose: () => void;
  onGemaakt: (dierId: number) => void;
}) {
  const t = useTranslate();
  const [form, setForm] = useState<Formulier>(LEEG);
  const [melding, setMelding] = useState<Melding>(null);
  const { mutate, isLoading: bezig } = useCustomMutation();

  const { data: soortenData } = useCustom<OptieRij[]>({
    url: "animals/reference/species",
    method: "get",
  });
  const { data: rassenData } = useCustom<OptieRij[]>({
    url: "animals/reference/breeds",
    method: "get",
  });
  const soorten = Array.isArray(soortenData?.data) ? soortenData.data : [];
  const rassen = Array.isArray(rassenData?.data) ? rassenData.data : [];

  const zet = <K extends keyof Formulier>(sleutel: K, waarde: Formulier[K]) => {
    setForm((f) => ({ ...f, [sleutel]: waarde }));
    setMelding(null);
  };

  function opslaan() {
    if (!form.naam.trim() || !form.diersoortId) {
      setMelding("verplicht");
      return;
    }
    const chip = form.chipPasnummer.trim();
    if (chip && !/^\d{15}$/.test(chip)) {
      setMelding("chip");
      return;
    }
    mutate(
      {
        url: "animals",
        method: "post",
        values: {
          naam: form.naam.trim(),
          diersoortId: Number(form.diersoortId),
          klantId,
          rasId: form.rasId ? Number(form.rasId) : null,
          geslacht: form.geslacht || null,
          gecastreerd: form.gecastreerd,
          geboortedatum: form.geboortedatum || null,
          chipPasnummer: chip || null,
        },
      },
      {
        onSuccess: (res) => {
          const id = (res?.data as { id?: number } | undefined)?.id;
          if (id != null) onGemaakt(id);
          else onClose();
        },
        onError: (err) => {
          const status = (err as { statusCode?: number } | undefined)?.statusCode;
          setMelding(status === 403 ? "verboden" : "fout");
        },
      },
    );
  }

  return (
    <Modal
      open
      title={t("dierCreate.titel")}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={bezig}>
            {t("common.cancel")}
          </Button>
          <Button variant="primary" onClick={opslaan} disabled={bezig}>
            {t("common.save")}
          </Button>
        </>
      }
    >
      <div className="fawkes-notes__form">
        {melding && (
          <span className="fawkes-notes__notice fawkes-notes__notice--required" role="alert">
            {t(`dierCreate.melding.${melding}`)}
          </span>
        )}
        <Field
          label={t("dierCreate.naam")}
          value={form.naam}
          maxLength={50}
          onChange={(e) => zet("naam", e.target.value)}
        />
        <SelectField
          label={t("dierCreate.diersoort")}
          value={form.diersoortId}
          options={[{ value: "", label: t("dierCreate.kies") }, ...opties(soorten)]}
          onChange={(e) => zet("diersoortId", e.target.value)}
        />
        <SelectField
          label={t("dierCreate.ras")}
          value={form.rasId}
          options={[{ value: "", label: t("dierCreate.onbekend") }, ...opties(rassen)]}
          onChange={(e) => zet("rasId", e.target.value)}
        />
        <SelectField
          label={t("dierCreate.geslacht")}
          value={form.geslacht}
          options={[
            { value: "", label: t("dierCreate.onbekend") },
            { value: "Mannelijk", label: t("dierCreate.mannelijk") },
            { value: "Vrouwelijk", label: t("dierCreate.vrouwelijk") },
          ]}
          onChange={(e) => zet("geslacht", e.target.value)}
        />
        <CheckboxField
          label={t("dierCreate.gecastreerd")}
          checked={form.gecastreerd}
          onChange={(e) => zet("gecastreerd", e.target.checked)}
        />
        <Field
          label={t("dierCreate.geboortedatum")}
          type="date"
          value={form.geboortedatum}
          onChange={(e) => zet("geboortedatum", e.target.value)}
        />
        <Field
          label={t("dierCreate.chip")}
          value={form.chipPasnummer}
          maxLength={15}
          inputMode="numeric"
          onChange={(e) => zet("chipPasnummer", e.target.value.replace(/\D/g, ""))}
        />
      </div>
    </Modal>
  );
}
